import React, { useRef, useEffect, useState } from 'react';
import { DB } from '../data/database';
import { ROLES, formatPrice, getYardLocations } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import StatCard from './ui/StatCard';
import {
  InventoryIcon, CarIcon, EngineIcon, PartsIcon,
  ContainerIcon, RequestIcon, UsersIcon, DollarSignIcon,
  BarChartIcon, ActivityIcon
} from './Icons';
import { Notifications, NotificationIcons } from '../data/notifications';
import Chart from 'chart.js/auto';
import DetailModal from './modals/DetailModal';
import YardDashboard from './YardDashboard';

// Greeting based on time of day
function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'dash_good_morning';
  if (h < 17) return 'dash_good_afternoon';
  return 'dash_good_evening';
}

// Build top-3 suggestions for a customer based on their purchase history
function getSuggestions(user, vehicles, engines, parts) {
  const myVehicles = vehicles.filter(v => v.assignedTo === user.id);
  const myEngines  = engines.filter(e => e.assignedTo === user.id);
  const myParts    = parts.filter(p => p.assignedTo === user.id);

  const available = (list) => list.filter(i => i.status === 'available' || i.status === 'japan_yard');

  const suggestions = [];

  // Most-purchased vehicle make → suggest similar available vehicles
  if (myVehicles.length > 0) {
    const makeCounts = {};
    myVehicles.forEach(v => { if (v.make) makeCounts[v.make] = (makeCounts[v.make] || 0) + 1; });
    const topMake = Object.entries(makeCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (topMake) {
      available(vehicles)
        .filter(v => v.make === topMake)
        .sort((a, b) => (b.priceJPY || 0) - (a.priceJPY || 0))
        .slice(0, 3)
        .forEach(v => suggestions.push({ ...v, _type: 'vehicles', _tag: `Based on your ${topMake}s` }));
    }
  }

  // Most-purchased engine manufacturer → suggest similar
  if (suggestions.length < 3 && myEngines.length > 0) {
    const mfrCounts = {};
    myEngines.forEach(e => { if (e.manufacturer) mfrCounts[e.manufacturer] = (mfrCounts[e.manufacturer] || 0) + 1; });
    const topMfr = Object.entries(mfrCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (topMfr) {
      available(engines)
        .filter(e => e.manufacturer === topMfr)
        .sort((a, b) => (b.priceJPY || 0) - (a.priceJPY || 0))
        .slice(0, 3 - suggestions.length)
        .forEach(e => suggestions.push({ ...e, _type: 'engines', _tag: `${topMfr} engines` }));
    }
  }

  // Most-purchased part category → suggest similar
  if (suggestions.length < 3 && myParts.length > 0) {
    const catCounts = {};
    myParts.forEach(p => { if (p.category) catCounts[p.category] = (catCounts[p.category] || 0) + 1; });
    const topCat = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (topCat) {
      available(parts)
        .filter(p => p.category === topCat)
        .sort((a, b) => (b.priceJPY || 0) - (a.priceJPY || 0))
        .slice(0, 3 - suggestions.length)
        .forEach(p => suggestions.push({ ...p, _type: 'parts', _tag: `Popular ${topCat}` }));
    }
  }

  // Fill remainder with highest-value available vehicles
  if (suggestions.length < 3) {
    const usedIds = new Set(suggestions.map(s => s.id));
    available(vehicles)
      .filter(v => !usedIds.has(v.id))
      .sort((a, b) => (b.priceJPY || 0) - (a.priceJPY || 0))
      .slice(0, 3 - suggestions.length)
      .forEach(v => suggestions.push({ ...v, _type: 'vehicles', _tag: 'Top pick' }));
  }

  return suggestions.slice(0, 3);
}

function CustomerDashboard({ user, currency, lang, setPage }) {
  const { t } = useLanguage();
  const [detail, setDetail] = useState(null);
  const [, forceUpdate]     = useState();

  const vehicles   = DB.get('vehicles')   || [];
  const engines    = DB.get('engines')    || [];
  const parts      = DB.get('spareParts') || [];
  const containers = DB.get('containers') || [];

  // Stock = customer_stock entries (wishlist)
  const customerStock = (DB.get('customerStock') || []).filter(s => s.customerId === user.id);
  const stockUnitIds = new Set(customerStock.map(s => s.unitId));
  const myVehicles = vehicles.filter(v => stockUnitIds.has(v.id));
  const myEngines  = engines.filter(e => stockUnitIds.has(e.id));
  const myParts    = parts.filter(p => stockUnitIds.has(p.id));

  // Also find units assigned to me (purchased/sold) for container tracking
  const ownedUnitIds = [...vehicles.filter(v => v.assignedTo === user.id), ...engines.filter(e => e.assignedTo === user.id), ...parts.filter(p => p.assignedTo === user.id)].map(u => u.id);
  const allMyUnitIds = [...new Set([...stockUnitIds, ...ownedUnitIds])];
  const myContainers = containers.filter(c => c.units?.some(uid => allMyUnitIds.includes(uid)));
  const containersOnWay  = myContainers.filter(c => ['in_transit', 'shipped', 'at_port'].includes(c.status));
  const containersArrived = myContainers.filter(c => ['delivered', 'arrived'].includes(c.status));

  const suggestions = getSuggestions(user, vehicles, engines, parts);

  const handleSuggestionUpdate = (updatedItem) => {
    if (!detail) return;
    const key = detail.type === 'vehicles' ? 'vehicles' : detail.type === 'engines' ? 'engines' : 'spareParts';
    const list = DB.get(key) || [];
    DB.set(key, list.map(i => i.id === updatedItem.id ? updatedItem : i));
    setDetail(prev => ({ ...prev, item: updatedItem }));
    forceUpdate({});
  };

  return (
    <div>
      {/* ── Welcome banner ── */}
      <div className="cust-welcome">
        <div className="cust-welcome-text">
          <div className="cust-welcome-greet">{t(getGreeting())},</div>
          <div className="cust-welcome-name">{user.name}</div>
          <div className="cust-welcome-sub">
            {myVehicles.length + myEngines.length + myParts.length} {t('dash_units_in_account')}
          </div>
        </div>
        <div className="cust-welcome-avatar">
          {user.avatar
            ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : user.name?.charAt(0).toUpperCase()}
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <StatCard icon={CarIcon}        value={myVehicles.length}           label={t('vehicles')}  color="blue"   onClick={() => setPage?.('myStock')} />
        <StatCard icon={EngineIcon}     value={myEngines.length}            label={t('engines')}   color="purple" onClick={() => setPage?.('myStock')} />
        <StatCard icon={PartsIcon}      value={myParts.length}              label={t('parts')}     color="yellow" onClick={() => setPage?.('myStock')} />
      </div>

      {/* ── Containers + Suggestions ── */}
      <div className="dash-two-col" style={{ alignItems: 'start' }}>

        {/* Container status */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <ContainerIcon />
              {t('dash_my_shipments')}
            </div>
          </div>
          <div className="card-body">
            <div className="cust-cont-row">
              <div className="cust-cont-stat">
                <div className="cust-cont-stat-val" style={{ color: 'var(--blue)' }}>
                  {containersOnWay.length}
                </div>
                <div className="cust-cont-stat-lbl">{t('dash_on_the_way')}</div>
              </div>
              <div className="cust-cont-stat">
                <div className="cust-cont-stat-val" style={{ color: 'var(--green)' }}>
                  {containersArrived.length}
                </div>
                <div className="cust-cont-stat-lbl">{t('dash_arrived')}</div>
              </div>
            </div>

            {myContainers.length === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', paddingTop: 12 }}>
                {t('dash_no_shipments')}
              </div>
            ) : (
              <div className="cust-cont-list">
                {myContainers.slice(0, 5).map(c => (
                  <div key={c.id} className="cust-cont-item">
                    <span className="cust-cont-item-icon"><ContainerIcon /></span>
                    <span className="cust-cont-number">{c.number}</span>
                    <span className={`status status-${c.status === 'in_transit' ? 'transit' : c.status === 'arrived' ? 'available' : 'pending'}`} style={{ fontSize: 10 }}>
                      {t('status_' + c.status) || c.status}
                    </span>
                    {c.eta && <span className="cust-cont-eta">ETA {c.eta}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top suggestions */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <InventoryIcon />
              {suggestions.length === 0 ? t('dash_available_units') : t('dash_recommended')}
            </div>
          </div>
          <div className="card-body">
            {suggestions.length === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 12 }}>{t('dash_no_available')}</div>
            ) : (
              <div className="cust-suggest-grid">
                {suggestions.map(item => {
                  const name = item.make ? `${item.make} ${item.model}` : item.engineName || item.partName;
                  return (
                    <div
                      key={item.id}
                      className="cust-suggest-card"
                      onClick={() => setDetail({ item, type: item._type })}
                    >
                      <div className="cust-suggest-img">
                        {item.images?.length
                          ? <img src={item.images[0]} alt={name} />
                          : (item._type === 'vehicles' ? <CarIcon /> : item._type === 'engines' ? <EngineIcon /> : <PartsIcon />)
                        }
                      </div>
                      <div className="cust-suggest-body">
                        <div className="cust-suggest-tag">{item._tag}</div>
                        <div className="cust-suggest-name">{name}</div>
                        <div className="cust-suggest-sku">{item.sku}</div>
                        <div className="cust-suggest-price" style={{ color: 'var(--text2)', fontSize: 11 }}>{item.city || item.port || ''}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail modal for suggestions */}
      {detail && (
        <DetailModal
          item={detail.item}
          type={detail.type}
          onClose={() => setDetail(null)}
          currency={currency}
          canPrice={true}
          isCustomer={true}
          lang={lang}
          onUpdate={handleSuggestionUpdate}
        />
      )}
    </div>
  );
}

function AccountantDashboard({ user, currency, lang }) {
  const { t } = useLanguage();
  const chartRef      = useRef(null);
  const chartInstance = useRef(null);

  const vehicles   = DB.get('vehicles')   || [];
  const engines    = DB.get('engines')    || [];
  const parts      = DB.get('spareParts') || [];
  const containers = DB.get('containers') || [];
  const allUnits   = [...vehicles, ...engines, ...parts];

  // ── Core financials ─────────────────────────────────────────
  const totalFOB  = allUnits.reduce((s, u) => s + (u.priceJPY || 0), 0);
  const totalCost = allUnits.reduce((s, u) => {
    return s +
      (Number(u.purchasePrice) || Number(u.costJPY) || 0) +
      (Number(u.auctionFee)        || 0) +
      (Number(u.domesticTransport) || 0) +
      (Number(u.inspectionFee)     || 0) +
      (Number(u.exportDocs)        || 0);
  }, 0);
  const grossMargin   = totalFOB > 0 ? ((totalFOB - totalCost) / totalFOB * 100).toFixed(1) : '0';
  const unpricedCount = allUnits.filter(u => !u.priceJPY).length;

  const vFOB = vehicles.filter(v => v.priceJPY > 0).reduce((s, v) => s + v.priceJPY, 0);
  const eFOB = engines.filter(e => e.priceJPY > 0).reduce((s, e) => s + e.priceJPY, 0);
  const pFOB = parts.filter(p => p.priceJPY > 0).reduce((s, p) => s + p.priceJPY, 0);

  // ── Container financials ─────────────────────────────────────
  const containerRows = containers.map(c => {
    const freight =
      (Number(c.freightCost)  || 0) + (Number(c.drayage)    || 0) +
      (Number(c.portCharges)  || 0) + (Number(c.blFee)      || 0) +
      (Number(c.loadingFee)   || 0);
    const unitIds = c.units || [];
    const unitFOB = allUnits.filter(u => unitIds.includes(u.id)).reduce((s, u) => s + (u.priceJPY || 0), 0);
    return { ...c, freight, unitFOB, unitCount: unitIds.length };
  });
  const totalFreight = containerRows.reduce((s, c) => s + c.freight, 0);

  // ── Chart: FOB value by vehicle make ────────────────────────
  const fobByMake = {};
  vehicles.forEach(v => {
    if (v.priceJPY > 0) fobByMake[v.make || 'Other'] = (fobByMake[v.make || 'Other'] || 0) + v.priceJPY;
  });
  const chartLabels = Object.keys(fobByMake).sort((a, b) => fobByMake[b] - fobByMake[a]).slice(0, 6);
  const chartData   = chartLabels.map(m => fobByMake[m]);

  useEffect(() => {
    if (!chartRef.current || chartLabels.length === 0) return;
    if (chartInstance.current) chartInstance.current.destroy();
    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels: chartLabels,
        datasets: [{
          data: chartData,
          backgroundColor: ['rgba(88,166,255,0.75)','rgba(63,185,80,0.75)','rgba(188,140,255,0.75)','rgba(230,162,60,0.75)','rgba(255,123,114,0.75)','rgba(52,211,153,0.75)'],
          borderRadius: 6,
          borderSkipped: false,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: ctx => ` ¥${Number(ctx.raw).toLocaleString()}` } },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(255,255,255,0.07)' },
            ticks: { color: '#8b949e', callback: v => `¥${(v / 1000000).toFixed(1)}M` },
          },
          x: { grid: { display: false }, ticks: { color: '#8b949e' } },
        },
      },
    });
    return () => { if (chartInstance.current) chartInstance.current.destroy(); };
  }, []); // data is read from DB once on mount

  // ── Top 5 units by FOB ──────────────────────────────────────
  const topUnits = [...allUnits]
    .filter(u => u.priceJPY > 0)
    .sort((a, b) => (b.priceJPY || 0) - (a.priceJPY || 0))
    .slice(0, 5);

  return (
    <div>
      {/* Greeting */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>{t('dashboard')}</h2>
        <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>
          {t(getGreeting())}, {user.name} — financial overview
        </div>
      </div>

      {/* Row 1: core money stats */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <StatCard icon={DollarSignIcon} value={formatPrice(totalFOB, currency)}   label={t('dash_total_fob')}   color="green"  />
        <StatCard icon={DollarSignIcon} value={formatPrice(totalCost, currency)}  label={t('dash_total_cost')}  color="blue"   />
        <StatCard icon={BarChartIcon}   value={`${grossMargin}%`}                 label={t('dash_gross_margin')}      color="purple" />
        <StatCard icon={InventoryIcon}  value={unpricedCount}                     label={t('dash_awaiting_pricing')}  color={unpricedCount > 0 ? 'orange' : 'green'} />
      </div>

      {/* Row 2: FOB by type + freight */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <StatCard icon={CarIcon}       value={formatPrice(vFOB, currency)}         label={t('dash_vehicle_fob')}  color="blue"   />
        <StatCard icon={EngineIcon}    value={formatPrice(eFOB, currency)}          label={t('dash_engine_fob')}   color="purple" />
        <StatCard icon={PartsIcon}     value={formatPrice(pFOB, currency)}          label={t('dash_parts_fob')}    color="yellow" />
        <StatCard icon={ContainerIcon} value={formatPrice(totalFreight, currency)}  label={t('dash_total_freight')} color="cyan"   />
      </div>

      <div className="dash-two-col" style={{ marginBottom: 20 }}>

        {/* FOB by make chart */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><BarChartIcon />{t('dash_fob_by_make')}</div>
          </div>
          <div className="card-body">
            {chartLabels.length === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: 40 }}>
                {t('dash_no_priced_vehicles')}
              </div>
            ) : (
              <div className="chart-box"><canvas ref={chartRef} /></div>
            )}
          </div>
        </div>

        {/* Top 5 units */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><DollarSignIcon />{t('dash_top_units')}</div>
          </div>
          <div className="card-body" style={{ padding: '4px 0' }}>
            {topUnits.length === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: 20 }}>
                {t('dash_no_priced_units')}
              </div>
            ) : topUnits.map((u, i) => {
              const name = u.make ? `${u.make} ${u.model}` : u.engineName || u.partName || 'Unknown';
              const cost =
                (Number(u.purchasePrice) || Number(u.costJPY) || 0) +
                (Number(u.auctionFee)        || 0) +
                (Number(u.domesticTransport) || 0) +
                (Number(u.inspectionFee)     || 0) +
                (Number(u.exportDocs)        || 0);
              const margin = cost > 0 && u.priceJPY > 0
                ? (((u.priceJPY - cost) / u.priceJPY) * 100).toFixed(0)
                : null;
              return (
                <div key={u.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 16px',
                  borderBottom: i < topUnits.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <span style={{ fontSize: 12, color: 'var(--text3)', minWidth: 16 }}>{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.sku}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>
                      {formatPrice(u.priceJPY, currency)}
                    </div>
                    {margin !== null && (
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>{margin}% margin</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Container financial breakdown */}
      <div className="card">
        <div className="card-header">
          <div className="card-title"><ContainerIcon />{t('dash_container_breakdown')}</div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {containerRows.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: 24 }}>
              {t('dash_no_containers')}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('dash_container')}</th>
                  <th>{t('dash_route')}</th>
                  <th>{t('dash_units')}</th>
                  <th>{t('dash_freight_cost')}</th>
                  <th>{t('dash_fob_value')}</th>
                  <th>{t('dash_net_after_freight')}</th>
                </tr>
              </thead>
              <tbody>
                {containerRows.map(c => {
                  const net = c.unitFOB - c.freight;
                  return (
                    <tr key={c.id}>
                      <td>
                        <strong style={{ fontFamily: 'monospace', color: 'var(--cyan)', fontSize: 12 }}>
                          {c.number}
                        </strong>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>
                        {c.origin} → {c.destination}
                      </td>
                      <td>{c.unitCount}</td>
                      <td style={{ color: 'var(--red)' }}>
                        {c.freight > 0 ? formatPrice(c.freight, currency) : '—'}
                      </td>
                      <td style={{ color: 'var(--green)', fontWeight: 600 }}>
                        {c.unitFOB > 0 ? formatPrice(c.unitFOB, currency) : '—'}
                      </td>
                      <td style={{
                        fontWeight: 700,
                        color: net > 0 ? 'var(--green)' : net < 0 ? 'var(--red)' : 'var(--text3)',
                      }}>
                        {c.freight > 0 || c.unitFOB > 0 ? formatPrice(net, currency) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Receivables + Pending Actions + Customer Balances ── */}
      {(() => {
        const allInv = DB.get('invoices') || [];
        const allPay = DB.get('payments') || [];
        const allCust = DB.get('customers') || [];
        const quotes = DB.get('priceQuotes') || [];
        const now = Date.now();
        const thisMonth = new Date(); thisMonth.setDate(1); thisMonth.setHours(0,0,0,0);

        const totalReceivable = allInv.reduce((s, i) => s + ((i.total || 0) - (i.amountPaid || 0)), 0);
        const currentDue = allInv.filter(i => ['sent', 'partial', 'viewed'].includes(i.status)).reduce((s, i) => s + ((i.total || 0) - (i.amountPaid || 0)), 0);
        const overdueTot = allInv.filter(i => (i.paymentMilestones || []).some(m => m.status !== 'paid' && m.due_date && new Date(m.due_date) < now))
          .reduce((s, i) => s + ((i.total || 0) - (i.amountPaid || 0)), 0);
        const overdueMs = allInv.flatMap(i => (i.paymentMilestones || []).filter(m => m.status !== 'paid' && m.due_date && new Date(m.due_date) < now));
        const oldestOverdueDays = overdueMs.length > 0 ? Math.max(...overdueMs.map(m => Math.floor((now - new Date(m.due_date).getTime()) / 86400000))) : 0;

        const monthInvoiced = allInv.filter(i => i.createdAt && new Date(i.createdAt) >= thisMonth).reduce((s, i) => s + (i.total || 0), 0);
        const monthReceived = allPay.filter(p => p.status === 'completed' && p.createdAt && new Date(p.createdAt) >= thisMonth).reduce((s, p) => s + (p.amount || 0), 0);
        const monthQuotes = quotes.filter(q => q.status === 'quoted' && q.quotedAt && new Date(q.quotedAt) >= thisMonth);
        const monthMargin = monthQuotes.length > 0 ? (monthQuotes.reduce((s, q) => s + (q.marginPercentage || 0), 0) / monthQuotes.length).toFixed(0) : '—';

        const pendingReceipts = allPay.filter(p => p.status === 'pending_verification').length;
        const pendingPricing = quotes.filter(q => q.status === 'pending').length;
        const overdueInv = allInv.filter(i => (i.paymentMilestones || []).some(m => m.status !== 'paid' && m.due_date && new Date(m.due_date) < now)).length;

        const custBalances = allCust.map(c => {
          const ci = allInv.filter(i => i.customerId === c.id);
          const inv = ci.reduce((s, i) => s + (i.total || 0), 0);
          const due = ci.reduce((s, i) => s + ((i.total || 0) - (i.amountPaid || 0)), 0);
          const hasOverdue = ci.some(i => (i.paymentMilestones || []).some(m => m.status !== 'paid' && m.due_date && new Date(m.due_date) < now));
          return { name: c.name, inv, due, status: due === 0 ? '✅' : hasOverdue ? '🔴' : '🟡' };
        }).filter(c => c.inv > 0).sort((a, b) => b.inv - a.inv);

        return (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
              {/* Receivables */}
              <div className="card">
                <div className="card-header"><div className="card-title"><DollarSignIcon />{t('dash_receivables')}</div></div>
                <div className="card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text2)' }}>{t('dash_total')}</span><strong>${totalReceivable.toLocaleString()}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text2)' }}>{t('dash_current')}</span><span style={{ color: 'var(--yellow)' }}>${currentDue.toLocaleString()}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text2)' }}>{t('dash_overdue')}</span><span style={{ color: 'var(--red)', fontWeight: 700 }}>${overdueTot.toLocaleString()}</span></div>
                    {oldestOverdueDays > 0 && <div style={{ fontSize: 10, color: 'var(--red)' }}>Oldest: {oldestOverdueDays} days</div>}
                  </div>
                </div>
              </div>

              {/* This Month */}
              <div className="card">
                <div className="card-header"><div className="card-title"><BarChartIcon />{t('dash_this_month')}</div></div>
                <div className="card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text2)' }}>{t('dash_invoiced')}</span><strong>${monthInvoiced.toLocaleString()}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text2)' }}>{t('dash_received')}</span><span style={{ color: 'var(--green)' }}>${monthReceived.toLocaleString()}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text2)' }}>{t('dash_avg_margin')}</span><span>{monthMargin}%</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text2)' }}>{t('dash_quotes_sent')}</span><span>{monthQuotes.length}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pending Actions */}
            {(pendingReceipts > 0 || pendingPricing > 0 || overdueInv > 0) && (
              <div className="card" style={{ marginTop: 16 }}>
                <div className="card-header"><div className="card-title">{t('dash_pending_actions')}</div></div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                  {pendingReceipts > 0 && <div style={{ color: 'var(--red)' }}>🔴 {pendingReceipts} payment receipt{pendingReceipts !== 1 ? 's' : ''} need verification</div>}
                  {pendingPricing > 0 && <div style={{ color: 'var(--yellow)' }}>🟡 {pendingPricing} unit{pendingPricing !== 1 ? 's' : ''} need pricing</div>}
                  {overdueInv > 0 && <div style={{ color: 'var(--orange)' }}>⚠️ {overdueInv} invoice{overdueInv !== 1 ? 's' : ''} overdue</div>}
                </div>
              </div>
            )}

            {/* Customer Balances */}
            {custBalances.length > 0 && (
              <div className="card" style={{ marginTop: 16 }}>
                <div className="card-header"><div className="card-title"><UsersIcon />{t('dash_customer_balances')}</div></div>
                <div className="card-body">
                  {custBalances.map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < custBalances.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
                      <span style={{ flex: 1, fontWeight: 600 }}>{c.name}</span>
                      <span style={{ color: 'var(--text3)', width: 100, textAlign: 'right' }}>${c.inv.toLocaleString()} inv</span>
                      <span style={{ color: c.due > 0 ? 'var(--yellow)' : 'var(--green)', width: 90, textAlign: 'right', fontWeight: 600 }}>${c.due.toLocaleString()} due</span>
                      <span style={{ width: 20, textAlign: 'center' }}>{c.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* ── MY QUOTES Widget (Accountant) ── */}
      {(() => {
        const quotes = DB.get('priceQuotes') || [];
        const now = Date.now();
        const thisWeek = quotes.filter(q => (q.status === 'approved' || q.status === 'quoted') && (q.approved_at || q.quotedAt) && (now - new Date(q.approved_at || q.quotedAt).getTime()) < 7 * 86400000);
        const pendingApproval = quotes.filter(q => q.status === 'pending_approval');
        const needsRevision = quotes.filter(q => q.status === 'revision_needed');
        const newRequests = quotes.filter(q => q.status === 'pending');

        return (
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-header">
              <div className="card-title"><DollarSignIcon /> My Quotes</div>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                <div style={{ textAlign: 'center', padding: 10, background: 'rgba(63,185,80,0.08)', borderRadius: 8 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)' }}>{thisWeek.length}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Approved this week</div>
                </div>
                <div style={{ textAlign: 'center', padding: 10, background: pendingApproval.length > 0 ? 'rgba(163,113,247,0.08)' : 'var(--bg3)', borderRadius: 8 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: pendingApproval.length > 0 ? 'var(--purple)' : 'var(--text3)' }}>{pendingApproval.length}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Pending approval</div>
                </div>
                <div style={{ textAlign: 'center', padding: 10, background: needsRevision.length > 0 ? 'rgba(248,81,73,0.08)' : 'var(--bg3)', borderRadius: 8 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: needsRevision.length > 0 ? 'var(--red)' : 'var(--text3)' }}>{needsRevision.length}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Needs revision</div>
                </div>
                <div style={{ textAlign: 'center', padding: 10, background: newRequests.length > 0 ? 'rgba(210,153,34,0.08)' : 'var(--bg3)', borderRadius: 8 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: newRequests.length > 0 ? 'var(--yellow)' : 'var(--text3)' }}>{newRequests.length}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>New to price</div>
                </div>
              </div>
              {/* Show revision notes if any */}
              {needsRevision.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {needsRevision.slice(0, 2).map(q => (
                    <div key={q.id} style={{ fontSize: 11, color: 'var(--red)', padding: '4px 0' }}>
                      🔴 #{q.id}: {q.revision_notes || q.admin_notes || 'Needs repricing'}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// YARD SALES DASHBOARD
// ══════════════════════════════════════════════════════════════
function YardSalesDashboard({ user, setPage }) {
  const { t } = useLanguage();
  const allBids = DB.get('yardBids') || [];
  const today = new Date().toISOString().split('T')[0];
  const now = Date.now();

  // Today's activity
  const todayBids = allBids.filter(b => (b.created_at || '').startsWith(today));
  const todayUnitIds = [...new Set(todayBids.map(b => b.unit_id))];
  const allSharjah = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])].filter(u => u.yard_location === 'sharjah_yard' && !['sold', 'sold_awaiting_payment', 'delivered', 'dismantled'].includes(u.status));
  const unitsWithBids = new Set(allBids.filter(b => b.status === 'active').map(b => b.unit_id));
  const noBids = allSharjah.filter(u => !unitsWithBids.has(u.id)).length;
  const highestToday = todayBids.length > 0 ? todayBids.reduce((max, b) => (b.amount || 0) > (max.amount || 0) ? b : max, todayBids[0]) : null;

  // My performance
  const weekAgo = new Date(now - 7 * 86400000).toISOString();
  const monthAgo = new Date(now - 30 * 86400000).toISOString();
  const myBids = allBids.filter(b => b.entered_by === user.id);
  const myWeek = myBids.filter(b => (b.created_at || '') >= weekAgo).length;
  const myMonth = myBids.filter(b => (b.created_at || '') >= monthAgo).length;
  const custAdded = (DB.get('customers') || []).filter(c => c.is_walk_in && (c.createdAt || '') >= weekAgo.split('T')[0]).length;

  // Streak calculation
  let streak = 0;
  for (let d = 0; d < 30; d++) {
    const day = new Date(now - d * 86400000).toISOString().split('T')[0];
    if (myBids.some(b => (b.created_at || '').startsWith(day))) streak++;
    else break;
  }

  // Pending sales
  const pendingSales = allBids.filter(b => b.selected_for_sale && b.status === 'active');

  // Recent bids (last 10)
  const recent = [...allBids].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 10);

  const timeAgo = (iso) => {
    if (!iso) return '';
    const ms = now - new Date(iso).getTime();
    if (ms < 60000) return 'just now';
    if (ms < 3600000) return `${Math.floor(ms / 60000)} min ago`;
    if (ms < 86400000) return `${Math.floor(ms / 3600000)}h ago`;
    return `${Math.floor(ms / 86400000)}d ago`;
  };

  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>{t(getGreeting())}, {user.name}</h2>

      {/* Today's Activity */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Today's Yard Activity</div></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
            <div style={{ textAlign: 'center', padding: 12, background: 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--blue)' }}>{todayBids.length}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Bids entered today</div>
            </div>
            <div style={{ textAlign: 'center', padding: 12, background: 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--green)' }}>{todayUnitIds.length}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Units with bids</div>
            </div>
            <div style={{ textAlign: 'center', padding: 12, background: noBids > 0 ? 'rgba(210,153,34,0.08)' : 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: noBids > 0 ? 'var(--yellow)' : 'var(--text3)' }}>{noBids}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Units with no bids</div>
            </div>
          </div>
          {highestToday && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text2)' }}>
              Highest today: <strong style={{ color: 'var(--green)' }}>{highestToday.currency} {highestToday.amount?.toLocaleString()}</strong> — {highestToday.unit_description}
            </div>
          )}
        </div>
      </div>

      {/* My Performance */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">My Performance</div></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
            <div style={{ textAlign: 'center', padding: 12, background: 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{myWeek}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Bids this week</div>
            </div>
            <div style={{ textAlign: 'center', padding: 12, background: 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{myMonth}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Bids this month</div>
            </div>
            <div style={{ textAlign: 'center', padding: 12, background: 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--cyan)' }}>{custAdded}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Customers added (week)</div>
            </div>
            <div style={{ textAlign: 'center', padding: 12, background: streak >= 5 ? 'rgba(248,81,73,0.08)' : 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: streak >= 5 ? 'var(--red)' : 'var(--text3)' }}>{streak}🔥</div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Day streak</div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Sales */}
      {pendingSales.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><div className="card-title">⏳ Pending Sales ({pendingSales.length})</div></div>
          <div className="card-body" style={{ padding: 0 }}>
            {pendingSales.map(b => (
              <div key={b.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{b.unit_description}</div>
                  <div style={{ color: 'var(--text3)', fontSize: 10 }}>→ {b.customer_name}</div>
                </div>
                <span style={{ fontWeight: 700, color: 'var(--yellow)' }}>{b.currency} {b.amount?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Arrivals Widget */}
      {(() => {
        const newArrivals = allSharjah.filter(u => {
          const at = u.arrived_at_yard || u.createdAt;
          return at && (now - new Date(at).getTime()) < 48 * 3600000;
        });
        if (newArrivals.length === 0) return null;
        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title">🆕 New Arrivals</div>
              {setPage && <button className="btn btn-secondary btn-sm" onClick={() => setPage('yardPricing')}>View</button>}
            </div>
            <div className="card-body">
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--blue)' }}>{newArrivals.length} unit{newArrivals.length !== 1 ? 's' : ''} arrived in last 48 hours</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{newArrivals.filter(u => (u.arrived_at_yard || u.createdAt || '').startsWith(today)).length} today · {newArrivals.length - newArrivals.filter(u => (u.arrived_at_yard || u.createdAt || '').startsWith(today)).length} yesterday</div>
            </div>
          </div>
        );
      })()}

      {/* No Bids — Needs Attention */}
      {(() => {
        const noBidUnits = allSharjah.filter(u => !['sold', 'sold_awaiting_payment', 'delivered', 'dismantled'].includes(u.status) && !(allBids.some(b => b.unit_id === u.id && b.status === 'active')));
        if (noBidUnits.length === 0) return null;
        const sorted = [...noBidUnits].sort((a, b) => {
          const dA = a.arrived_at_yard || a.createdAt || '';
          const dB = b.arrived_at_yard || b.createdAt || '';
          return dA.localeCompare(dB); // oldest first
        });
        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title">No Bids — Needs Attention ({noBidUnits.length})</div></div>
            <div className="card-body" style={{ padding: 0 }}>
              {sorted.slice(0, 8).map(u => {
                const at = u.arrived_at_yard || u.createdAt;
                const days = at ? Math.floor((now - new Date(at).getTime()) / 86400000) : 0;
                return (
                  <div key={u.id} style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span>{u.make ? `${u.make} ${u.model || ''}` : u.engineName || u.partName || 'Unit'}</span>
                    <span style={{ color: days >= 14 ? 'var(--red)' : days >= 7 ? 'var(--yellow)' : 'var(--text3)', fontWeight: days >= 7 ? 700 : 400 }}>
                      {days >= 14 ? '🔴' : days >= 7 ? '⚠️' : ''} {days}d in yard
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Recent Bids */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Recent Bids</div>
          {setPage && <button className="btn btn-secondary btn-sm" onClick={() => setPage('yardPricing')}>Go to Yard Pricing</button>}
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {recent.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No bids yet. Start scanning units!</div>
          ) : recent.map(b => (
            <div key={b.id} style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span style={{ color: 'var(--text3)', fontSize: 10, width: 70, flexShrink: 0 }}>{timeAgo(b.created_at)}</span>
              <span style={{ fontWeight: 600, flex: 1 }}>{b.customer_name}</span>
              <span style={{ fontWeight: 700, color: 'var(--green)' }}>{b.currency} {b.amount?.toLocaleString()}</span>
              <span style={{ color: 'var(--text3)', fontSize: 10 }}>{b.unit_description?.split(' ').slice(0, 3).join(' ')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ user, currency, lang, setPage, custFilter, setCustFilter }) {
  const { t } = useLanguage();
  const chartRef      = useRef(null);
  const chartInstance = useRef(null);
  const role          = ROLES[user.role] || {};
  const [assignDetail, setAssignDetail] = useState(null);

  if (role.isCustomer) {
    return <CustomerDashboard user={user} currency={currency} lang={lang} setPage={setPage} />;
  }

  if (user.role === 'accountant') {
    return <AccountantDashboard user={user} currency={currency} lang={lang} />;
  }

  if (user.role === 'yard_sales') {
    return <YardSalesDashboard user={user} setPage={setPage} />;
  }

  if (user.role === 'yard_operator' || user.role === 'yard_manager') {
    return <YardDashboard user={user} currency={currency} lang={lang} />;
  }

  const vehicles   = DB.get('vehicles')   || [];
  const engines    = DB.get('engines')    || [];
  const parts      = DB.get('spareParts') || [];
  const containers = DB.get('containers') || [];
  const requests   = DB.get('requests')   || [];
  const customers  = DB.get('customers')  || [];

  const totalValue = [
    ...vehicles.map(v => v.priceJPY || 0),
    ...engines.map(e => e.priceJPY  || 0),
    ...parts.map(p => p.priceJPY    || 0),
  ].reduce((sum, val) => sum + val, 0);

  // Real notifications for activity feed
  const recentActivity = Notifications.getAll()
    .filter(n => !n.targetUserId) // staff-visible only
    .slice(0, 8);

  // Customer ranking: most units → least, with country
  const allUnits = [...vehicles, ...engines, ...parts];
  const custData = customers
    .map(c => {
      const units = allUnits.filter(u => u.assignedTo === c.id);
      return { name: c.name, country: c.country || '', count: units.length };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const chartColors = ['#58a6ff','#3fb950','#bc8cff','#e6a23c','#ff7b72','#34d399','#d29922','#a371f7','#39c5cf','#db6d28'];

  useEffect(() => {
    if (!chartRef.current || custData.length === 0) return;
    if (chartInstance.current) chartInstance.current.destroy();
    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels: custData.map(c => `${c.name} (${c.country})`),
        datasets: [{
          label: 'Units',
          data: custData.map(c => c.count),
          backgroundColor: chartColors.slice(0, custData.length),
          borderRadius: 6,
          borderSkipped: false,
        }],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                const i = items[0].dataIndex;
                return `${custData[i].name}`;
              },
              label: (ctx) => {
                const i = ctx.dataIndex;
                return [
                  ` ${ctx.raw} units`,
                  ` Country: ${custData[i].country || '—'}`,
                ];
              },
            },
          },
        },
        scales: {
          x: { beginAtZero: true, grid: { color: 'rgba(255,255,255,0.07)' }, ticks: { color: '#8b949e', stepSize: 1 } },
          y: { grid: { display: false }, ticks: { color: '#c9d1d9', font: { size: 12 } } },
        },
      },
    });
    return () => { if (chartInstance.current) chartInstance.current.destroy(); };
  }, []);

  return (
    <div>
      <div className="stats-grid">
        <StatCard icon={InventoryIcon}  value={vehicles.length + engines.length + parts.length} label={t('totalUnits')}     color="blue"   onClick={() => setPage?.('inventory')} />
        <StatCard icon={CarIcon}        value={vehicles.length}                                  label={t('vehicles')}        color="green"  onClick={() => setPage?.('inventory')} />
        <StatCard icon={ContainerIcon}  value={containers.filter(c => c.status === 'in_transit').length} label={t('inTransit')} color="cyan" onClick={() => setPage?.('containers')} />
        <StatCard icon={DollarSignIcon} value={formatPrice(totalValue, currency)}                label={t('inventoryValue')}  color="green"  onClick={() => setPage?.('inventory')} />
      </div>

      <div className="stats-grid">
        <StatCard icon={EngineIcon}  value={engines.length}                                  label={t('engines')}         color="purple" onClick={() => setPage?.('inventory')} />
        <StatCard icon={PartsIcon}   value={parts.length}                                    label={t('parts')}           color="yellow" onClick={() => setPage?.('inventory')} />
        <StatCard icon={RequestIcon} value={requests.filter(r => r.status === 'pending').length} label={t('pendingRequests')} color="orange" onClick={() => setPage?.('requests')} />
        <StatCard icon={UsersIcon}   value={customers.length}                                label={t('customers')}       color="purple" onClick={() => setPage?.('customers')} />
      </div>

      {/* ── Overdue Payments Alert ── */}
      {(() => {
        const allInvoices = DB.get('invoices') || [];
        const now = Date.now();
        const overdueItems = [];
        allInvoices.forEach(inv => {
          if (inv.status === 'paid' || inv.status === 'cancelled' || inv.status === 'draft') return;
          (Array.isArray(inv.paymentMilestones) ? inv.paymentMilestones : []).forEach(m => {
            if (m.status === 'paid' || !m.due_date) return;
            const due = new Date(m.due_date).getTime();
            if (due < now) {
              const daysOverdue = Math.floor((now - due) / 86400000);
              overdueItems.push({
                customer: inv.customerName,
                invoiceNumber: inv.invoiceNumber,
                amount: m.amount || 0,
                daysOverdue,
                invoiceId: inv.id,
              });
            }
          });
        });
        overdueItems.sort((a, b) => b.daysOverdue - a.daysOverdue);

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title"><DollarSignIcon />{t('dash_overdue')}</div>
              <span style={{ fontSize: 11, color: overdueItems.length > 0 ? 'var(--red)' : 'var(--green)', fontWeight: 600 }}>
                {overdueItems.length > 0 ? `${overdueItems.length} overdue` : 'All payments current'}
              </span>
            </div>
            <div className="card-body" style={{ padding: overdueItems.length > 0 ? '0' : '14px 20px' }}>
              {overdueItems.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--green)', fontSize: 13 }}>All payments current</div>
              ) : (
                overdueItems.slice(0, 10).map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                    borderBottom: i < Math.min(overdueItems.length, 10) - 1 ? '1px solid var(--border)' : 'none',
                    cursor: 'pointer',
                  }} onClick={() => setPage?.('invoices')}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{item.customer}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>{item.invoiceNumber}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)' }}>${item.amount.toLocaleString()}</div>
                      <div style={{ fontSize: 10, color: 'var(--red)' }}>{item.daysOverdue}d overdue</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Yard Inspections — To Assign (admin only) ── */}
      {(user.role === 'admin' || user.role === 'head_admin') && (() => {
        const toAssign = [...vehicles, ...engines, ...parts].filter(u =>
          !u.destination_country &&
          (u.created_by_role === 'yard_operator' || u.created_by_role === 'yard_manager')
        );
        if (toAssign.length === 0) return null;
        const byYard = {};
        toAssign.forEach(u => { const y = u.destination_yard || 'unassigned'; (byYard[y] = byYard[y] || []).push(u); });
        const yardLabel = (id) => {
          if (id === 'unassigned') return { name: 'Unassigned', flag: '📍' };
          const y = getYardLocations().find(l => l.id === id);
          return y ? { name: y.name, flag: y.flag } : { name: id, flag: '📍' };
        };
        const unitName = (u) => u.make ? `${u.make} ${u.model || ''}`.trim() : u.engineName || u.partName || 'Unit';
        const detectType = (u) => u.engineName ? 'engines' : u.partName ? 'parts' : 'vehicles';
        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title"><InventoryIcon /> Yard Inspections — To Assign</div>
              <span style={{ fontSize: 11, color: 'var(--yellow)', fontWeight: 600 }}>{toAssign.length} awaiting</span>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {Object.entries(byYard).map(([yardId, units]) => {
                const yl = yardLabel(yardId);
                return (
                  <div key={yardId}>
                    <div style={{ padding: '8px 16px', background: 'var(--bg3)', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 16 }}>{yl.flag}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1, #e6edf3)' }}>{yl.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--text3, #8b949e)' }}>{units.length} unit{units.length !== 1 ? 's' : ''}</span>
                    </div>
                    {units.map((u, i) => (
                      <div key={u.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                        borderBottom: i < units.length - 1 ? '1px solid var(--border)' : 'none',
                        cursor: 'pointer',
                      }} onClick={() => setAssignDetail({ item: u, type: detectType(u) })}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1, #e6edf3)' }}>{unitName(u)}</div>
                          <div style={{ fontSize: 10, color: 'var(--text3, #8b949e)' }}>{u.sku}</div>
                        </div>
                        {u.created_by_name && (
                          <span style={{ fontSize: 11, color: 'var(--text3, #8b949e)' }}>by {u.created_by_name}</span>
                        )}
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: 'rgba(88,166,255,0.15)', color: 'var(--blue, #58a6ff)' }}>
                          Inspected
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="dash-two-col">
        {/* Customer units chart */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><UsersIcon />{t('dash_top_customers')}</div>
          </div>
          <div className="card-body">
            {custData.length === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: 40 }}>
                {t('dash_no_customers')}
              </div>
            ) : (
              <div className="chart-box"><canvas ref={chartRef} /></div>
            )}
          </div>
        </div>

        {/* ── Price Quotes Widget ── */}
        {(() => {
          const pq = DB.get('priceQuotes') || [];
          const awaitingApproval = pq.filter(q => q.status === 'pending_approval');
          const beingPriced = pq.filter(q => ['pending', 'in_progress'].includes(q.status));
          const beingRepriced = pq.filter(q => q.status === 'revision_needed');
          const completedThisMonth = pq.filter(q => {
            if (q.status !== 'approved' && q.status !== 'quoted' && q.status !== 'partially_approved') return false;
            const d = q.approved_at || q.quotedAt;
            if (!d) return false;
            const now = new Date();
            const dt = new Date(d);
            return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
          });

          return (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div className="card-title"><DollarSignIcon /> Price Quotes</div>
                {setPage && <button className="btn btn-secondary btn-sm" onClick={() => setPage('priceQuotes')}>View All</button>}
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                  <div style={{ textAlign: 'center', padding: 10, background: awaitingApproval.length > 0 ? 'rgba(163,113,247,0.08)' : 'var(--bg3)', borderRadius: 8, cursor: awaitingApproval.length > 0 ? 'pointer' : 'default' }}
                    onClick={() => awaitingApproval.length > 0 && setPage?.('priceQuotes')}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: awaitingApproval.length > 0 ? 'var(--purple)' : 'var(--text3)' }}>{awaitingApproval.length}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>Awaiting your approval</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: 10, background: beingPriced.length > 0 ? 'rgba(210,153,34,0.08)' : 'var(--bg3)', borderRadius: 8 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: beingPriced.length > 0 ? 'var(--yellow)' : 'var(--text3)' }}>{beingPriced.length}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>Being priced</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: 10, background: beingRepriced.length > 0 ? 'rgba(248,81,73,0.08)' : 'var(--bg3)', borderRadius: 8 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: beingRepriced.length > 0 ? 'var(--red)' : 'var(--text3)' }}>{beingRepriced.length}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>Being repriced</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: 10, background: 'var(--bg3)', borderRadius: 8 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)' }}>{completedThisMonth.length}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>Completed this month</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Supplier Metrics Widget (admin/head_admin only) ── */}
        {(user.role === 'admin' || user.role === 'head_admin') && (() => {
          const allU = [...vehicles, ...engines, ...parts];
          const monthStart = new Date(); monthStart.setDate(1); const ms = monthStart.toISOString().split('T')[0];
          const thisMonth = allU.filter(u => (u.createdAt || '') >= ms && u.purchase_supplier);
          const bySupplier = {};
          thisMonth.forEach(u => {
            const s = u.purchase_supplier;
            if (!s) return;
            if (!bySupplier[s]) bySupplier[s] = { count: 0, cost: 0 };
            bySupplier[s].count++;
            bySupplier[s].cost += (u.costJPY || 0);
          });
          const sorted = Object.entries(bySupplier).sort((a, b) => b[1].count - a[1].count);
          const totalUnits = sorted.reduce((s, [, v]) => s + v.count, 0);
          const totalCost = sorted.reduce((s, [, v]) => s + v.cost, 0);
          if (sorted.length === 0) return null;
          return (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header"><div className="card-title"><DollarSignIcon /> Supplier Metrics — This Month</div></div>
              <div className="card-body" style={{ padding: 0 }}>
                {sorted.slice(0, 5).map(([name, data], i) => (
                  <div key={name} style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span>{i + 1}. {name}</span>
                    <span style={{ color: 'var(--text3)' }}>{data.count} units — ¥{data.cost.toLocaleString()}</span>
                  </div>
                ))}
                <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--text3)' }}>
                  {sorted.length} suppliers · {totalUnits} units · ¥{totalCost.toLocaleString()}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Yard Sales Widget ── */}
        {(() => {
          const yb = DB.get('yardBids') || [];
          const today = new Date().toISOString().split('T')[0];
          const todayWon = yb.filter(b => b.status === 'won' && (b.sale_approved_at || '').startsWith(today));
          const todayBids = yb.filter(b => (b.created_at || '').startsWith(today));
          const pendingPay = yb.filter(b => b.status === 'won' && b.payment_status !== 'paid');
          const todayRevenue = todayWon.reduce((s, b) => s + (b.amount_usd || 0), 0);
          const topSale = todayWon.length > 0 ? todayWon.reduce((max, b) => (b.amount_usd || 0) > (max.amount_usd || 0) ? b : max, todayWon[0]) : null;

          return (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div className="card-title"><DollarSignIcon /> Yard Sales — Today</div>
                {setPage && <button className="btn btn-secondary btn-sm" onClick={() => setPage('yardPricing')}>View Yard Pricing</button>}
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text2)', flexWrap: 'wrap', marginBottom: topSale ? 8 : 0 }}>
                  <span>Units sold: <strong style={{ color: 'var(--green)' }}>{todayWon.length}</strong></span>
                  <span>Revenue: <strong style={{ color: 'var(--green)' }}>${todayRevenue.toLocaleString()}</strong></span>
                  <span>Bids received: <strong>{todayBids.length}</strong></span>
                  <span>Pending payment: <strong style={{ color: pendingPay.length > 0 ? 'var(--yellow)' : 'var(--text3)' }}>{pendingPay.length}</strong></span>
                </div>
                {topSale && (
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                    Top sale: {topSale.unit_description} — ${(topSale.amount_usd || 0).toLocaleString()} to {topSale.customer_name}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── All Customer Stock Overview (admin only) ── */}
        {(user.role === 'admin' || user.role === 'head_admin') && (() => {
          const stock = DB.get('customerStock') || [];
          const byCustomer = {};
          stock.forEach(s => {
            const cust = customers.find(c => c.id === s.customerId);
            const name = cust?.name || 'Unknown';
            if (!byCustomer[name]) byCustomer[name] = { count: 0, customerId: s.customerId };
            byCustomer[name].count++;
          });
          const sorted = Object.entries(byCustomer).sort((a, b) => b[1].count - a[1].count);
          const totalStock = stock.length;

          // Pending quotations
          const pq = DB.get('priceQuotes') || [];
          const pendingPricing = pq.filter(q => ['pending', 'in_progress'].includes(q.status));
          const pendingUnits = pendingPricing.reduce((s, q) => s + (q.units || []).filter(u => !u.fob_price).length, 0);

          // Pending selections
          const so = DB.get('selectionOrders') || [];
          const pendingSelections = so.filter(o => ['submitted', 'under_review'].includes(o.status));

          return (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <div className="card-title"><UsersIcon /> Customer Stock & Requests</div>
                {setPage && <button className="btn btn-secondary btn-sm" onClick={() => setPage('customers')}>View Customers</button>}
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
                  <div style={{ textAlign: 'center', padding: 12, background: 'var(--bg3)', borderRadius: 8, cursor: 'pointer' }} onClick={() => setPage?.('customers')}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--blue)' }}>{totalStock}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>Units in customer stock</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: 12, background: pendingUnits > 0 ? 'rgba(210,153,34,0.08)' : 'var(--bg3)', borderRadius: 8, cursor: 'pointer' }} onClick={() => setPage?.('priceQuotes')}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: pendingUnits > 0 ? 'var(--yellow)' : 'var(--text3)' }}>{pendingUnits}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>Awaiting pricing</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: 12, background: pendingSelections.length > 0 ? 'rgba(163,113,247,0.08)' : 'var(--bg3)', borderRadius: 8, cursor: 'pointer' }} onClick={() => setPage?.('selectionOrders')}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: pendingSelections.length > 0 ? 'var(--purple)' : 'var(--text3)' }}>{pendingSelections.length}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>Pending selections</div>
                  </div>
                </div>
                {sorted.length > 0 && (
                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Top customers by stock</div>
                )}
                {sorted.slice(0, 5).map(([name, data], i) => (
                  <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < Math.min(sorted.length, 5) - 1 ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
                    <span style={{ color: 'var(--text1)' }}>{name}</span>
                    <span style={{ color: 'var(--text3)' }}>{data.count} unit{data.count !== 1 ? 's' : ''}</span>
                  </div>
                ))}
                {sorted.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: 10 }}>No customer stock entries yet</div>}
              </div>
            </div>
          );
        })()}

        {/* Real recent activity from notifications */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><ActivityIcon />{t('recentActivity')}</div>
          </div>
          <div className="card-body">
            {recentActivity.length === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                {t('dash_no_activity')}
              </div>
            ) : (
              recentActivity.map(n => (
                <div key={n.id} className="activity-item">
                  <div className="activity-icon blue">
                    <span style={{ fontSize: 16 }}>{NotificationIcons[n.type] || '📋'}</span>
                  </div>
                  <div>
                    <div className="activity-text">{n.message}</div>
                    <div className="activity-time">{Notifications.timeAgo(n.createdAt)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Customer Overview Widget ── */}
      {(() => {
        const allInvoices = DB.get('invoices') || [];
        const selOrders = DB.get('selectionOrders') || [];
        const now = Date.now();

        // Top customers by revenue
        const custRevenue = customers.map(c => {
          const ci = allInvoices.filter(i => i.customerId === c.id);
          const invoiced = ci.reduce((s, i) => s + (i.total || 0), 0);
          const paid = ci.reduce((s, i) => s + (i.amountPaid || 0), 0);
          const outstanding = invoiced - paid;
          const overdue = ci.filter(i => (i.paymentMilestones || []).some(m => m.status !== 'paid' && m.due_date && new Date(m.due_date) < now))
            .reduce((s, i) => s + ((i.total || 0) - (i.amountPaid || 0)), 0);
          const unitCount = [...vehicles, ...engines, ...parts].filter(u => u.assignedTo === c.id).length;
          const contCount = containers.filter(cont => (cont.units || []).some(uid => {
            const u = [...vehicles, ...engines, ...parts].find(x => x.id === uid);
            return u?.assignedTo === c.id;
          })).length;
          const pendingSel = selOrders.filter(o => o.customerId === c.id && o.status === 'submitted').length;
          const oldestSelDays = selOrders.filter(o => o.customerId === c.id && o.status === 'submitted' && o.submittedAt)
            .reduce((max, o) => Math.max(max, Math.floor((now - new Date(o.submittedAt).getTime()) / 86400000)), 0);
          return { ...c, invoiced, paid, outstanding, overdue, unitCount, contCount, pendingSel, oldestSelDays };
        }).filter(c => c.invoiced > 0 || c.unitCount > 0).sort((a, b) => b.invoiced - a.invoiced);

        const attention = [];
        custRevenue.forEach(c => {
          if (c.overdue > 0) attention.push({ type: 'overdue', text: `${c.name} — $${c.overdue.toLocaleString()} overdue`, color: 'var(--red)' });
          if (c.pendingSel > 0) attention.push({ type: 'pending', text: `${c.name} — ${c.pendingSel} pending selection${c.pendingSel > 1 ? 's' : ''}${c.oldestSelDays > 2 ? ` (no action ${c.oldestSelDays}d)` : ''}`, color: 'var(--yellow)' });
        });

        return (
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-header">
              <div className="card-title"><UsersIcon />{t('dash_customer_overview')}</div>
            </div>
            <div className="card-body">
              {custRevenue.length > 0 && (
                <div style={{ marginBottom: attention.length > 0 ? 16 : 0 }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{t('dash_top_customers')}</div>
                  {custRevenue.slice(0, 5).map((c, i) => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < Math.min(custRevenue.length, 5) - 1 ? '1px solid var(--border)' : 'none' }}>
                      <span style={{ fontSize: 12, color: 'var(--text3)', width: 16 }}>{i + 1}.</span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{c.name}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>${c.invoiced.toLocaleString()}</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)', width: 70, textAlign: 'right' }}>{c.unitCount} units</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)', width: 80, textAlign: 'right' }}>{c.contCount} container{c.contCount !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              )}
              {attention.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>⚠️ Attention</div>
                  {attention.map((a, i) => (
                    <div key={i} style={{ fontSize: 12, color: a.color, padding: '4px 0' }}>{a.text}</div>
                  ))}
                </div>
              )}
              {custRevenue.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', padding: 16 }}>{t('dash_no_customer_data')}</div>}
            </div>
          </div>
        );
      })()}

      {assignDetail && (
        <DetailModal
          item={assignDetail.item}
          type={assignDetail.type}
          onClose={() => setAssignDetail(null)}
          currency={currency}
          canPrice={true}
          isCustomer={false}
          lang={lang}
          user={user}
          onUpdate={(updated) => {
            const key = assignDetail.type === 'vehicles' ? 'vehicles' : assignDetail.type === 'engines' ? 'engines' : 'spareParts';
            const list = DB.get(key) || [];
            DB.set(key, list.map(u => u.id === updated.id ? updated : u));
            setAssignDetail(null);
          }}
        />
      )}
    </div>
  );
}

export default Dashboard;
